import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    ScrollView,
    View
} from "react-native";
import type {
    CompanyItem
} from "./type";
import stylesheet from "./stylesheet";
import CompanyCard from "./components/companyCard";
import CompanyFilters from "./components/companyFilters";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Button,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    PlusIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";

const CompaniesList = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 992;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        debouncedSectorFilters,
        setDebouncedSectorFilters
    ] = useState<string[]>([]);

    const [
        debouncedSearchQuery,
        setDebouncedSearchQuery
    ] = useState<string>("");

    const [
        sectorFilters,
        setSectorFilters
    ] = useState<string[]>([]);

    const [
        companies,
        setCompanies
    ] = useState<CompanyItem[]>([]);

    const [
        sectorData,
        setSectorData
    ] = useState<Array<{ title: string; value: string }>>([]);

    const [
        sectorMap,
        setSectorMap
    ] = useState<Record<string, string>>({});

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const scrollViewRef = useRef<ScrollView>(null);

    const fetchCompanies = async (currentPage: number, search: string, sectorsList: string[]) => {
        try {
            setLoading(true);
            setCompanies([]);

            const result = await RESTServiceContext.action("getAllCompaniesForAdmin", {
                sectorIDs: sectorsList.length > 0 ? sectorsList : undefined,
                searchText: search || undefined,
                page: currentPage
            });

            if(result.payload) {
                setCompanies(result.payload as unknown as CompanyItem[]);
                if (result.totalCount !== undefined) {
                    setTotalCount(result.totalCount);
                }
            } else {
                setCompanies([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSectors = async () => {
        try {
            const result = await RESTServiceContext.action("getSectors", null);
            if (result.payload) {
                const mappedData = result.payload.map(sector => ({
                    title: sector.value,
                    value: sector._id
                }));
                const map: Record<string, string> = {};
                result.payload.forEach(s => {
                    map[s._id] = s.value;
                });

                setSectorData(mappedData);
                setSectorMap(map);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSectorFilters(prev => {
                if (prev.length === sectorFilters.length && prev.every((v, i) => v === sectorFilters[i])) {
                    return prev;
                }
                return sectorFilters;
            });
            setDebouncedSearchQuery(prev => prev !== searchQuery ? searchQuery : prev);
        }, 500);

        return () => clearTimeout(timer);
    }, [
        sectorFilters,
        searchQuery
    ]);

    useEffect(() => {
        fetchSectors();
    }, []);

    useFocusEffect(
        useCallback(() => {
            fetchCompanies(1, debouncedSearchQuery, debouncedSectorFilters);
            setPage(1);
        }, [
            debouncedSectorFilters,
            debouncedSearchQuery
        ])
    );

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("companies-page-title")}
            </Text>
            <Button
                spreadBehaviour={isMobile ? "stretch" : "free"}
                customTextColor={colors.project.brandWhite}
                onPress={() => {
                    navigation.navigate("CompanyCreate");
                }}
                title={localize("companies-create-new")}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                customColor="gold"
            />
        </View>;
    };

    const renderActiveChips = () => {
        if (sectorFilters.length === 0) {
            return null;
        }

        return <View
            style={[
                stylesheet.activeChipsContainer,
                {
                    marginTop: spaces.spacingMd,
                    gap: spaces.spacingSm
                }
            ]}
        >
            {sectorFilters.map(sf => {
                const sectorTitle = sectorMap[sf];
                if (!sectorTitle) return null;

                return <Chip
                    onPress={() => setSectorFilters(prev => prev.filter(f => f !== sf))}
                    key={`active-chip-${sf}`}
                    customColor="primary"
                    title={sectorTitle}
                    isClosable={true}
                    size="small"
                />;
            })}
        </View>;
    };

    const renderTableHeader = () => {
        if(isMobile) {
            return null;
        }

        return <View
            style={[
                stylesheet.tableHeaderRow,
                {
                    borderBottomColor: colors.project.brandBorder,
                    borderBottomWidth: borders.seperator,
                    paddingVertical: spaces.spacingMd,
                    marginBottom: spaces.spacingMd,
                    marginTop: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.colCompany,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("companies-form-name")}
                </Text>
            </View>
            <View
                style={stylesheet.colContact}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("companies-form-email")}
                </Text>
            </View>
            <View
                style={stylesheet.colSector}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("companies-form-sectors")}
                </Text>
            </View>
            <View
                style={stylesheet.colActions}
            />
        </View>;
    };

    const renderEmpty = () => {
        if(loading) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl,
                        flex: 1
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        return <View
            style={[
                stylesheet.emptyContainer,
                {
                    padding: spaces.spacingXl,
                    flex: 1
                }
            ]}
        >
            <Text
                style={[
                    stylesheet.emptyText,
                    {
                        color: colors.content.text.mid
                    }
                ]}
                variant="bodyLargeSize"
            >
                {localize("companies-empty-list")}
            </Text>
        </View>;
    };

    const renderPagination = () => {
        return <View
            style={[
                stylesheet.paginationContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Pagination
                onSelect={(item) => {
                    setPage(item.pageNumber);
                    if (scrollViewRef.current) {
                        scrollViewRef.current.scrollTo({
                            animated: false,
                            y: 0
                        });
                    }
                    fetchCompanies(item.pageNumber, debouncedSearchQuery, debouncedSectorFilters);
                }}
                totalDataCount={totalCount}
                currentPage={page}
                itemPerPage={20}
            />
        </View>;
    };

    const renderContent = () => {
        return <View
            style={stylesheet.listWrapper}
        >
            <ScrollView
                contentContainerStyle={{
                    paddingBottom: spaces.spacingXl,
                    flexGrow: 1
                }}
                ref={scrollViewRef}
            >
                {(!loading && companies.length === 0) ? null : renderTableHeader()}
                {
                    companies.length === 0 ? renderEmpty() : companies.map((item, index) => {
                        return <CompanyCard
                            key={`company-card-${item._id}`}
                            sectorMap={sectorMap}
                            company={item}
                            index={index}
                        />;
                    })
                }
                {renderPagination()}
            </ScrollView>
        </View>;
    };

    return <PageContainer
        isScrollable={false}
    >
        <View
            style={[
                stylesheet.container,
                {
                    paddingHorizontal: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    paddingTop: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    gap: isMobile ? spaces.spacingMd : spaces.spacingLg
                }
            ]}
        >
            {renderHeader()}
            <CompanyFilters
                setSectorFilters={setSectorFilters}
                setSearchQuery={setSearchQuery}
                sectorFilters={sectorFilters}
                searchQuery={searchQuery}
                sectorData={sectorData}
            />
            {renderActiveChips()}
            {renderContent()}
        </View>
    </PageContainer>;
};

export default CompaniesList;
